import { journalBoundaryContext, boundaryValue, boundarySource } from '../ability/boundary-journal.js';
import { type JanusCtx, type Row, type LogRow, HandBack, Refusal, activeStatuses, ec, ecId, ecLean, inventoryId, itemField, record, scalar, upper } from '../ability/context.js';
import { frameEcChanges } from '../ability/resolved-verdict.js';
import { composeActualDeath, departSummon, removalSummons } from '../death/compose.js';
import { composeCreatureDeletion22 } from '../death/delete-plan-22.js';
import { deathStatusRows } from '../death/cleanup.js';
import { deathHolders } from '../death/departure.js';
import { applyStatusEffect, QUANTITY_FAMILIES } from '../ability/statuses/apply-status-effect.js';
import { removeStoredStatus, connectionSurvivors, storedStatusHasSpecialTeardown, storedStatusHasUnportedTeardown, resolveStatusRemovalSpecials } from '../ability/statuses/status-effect-removal-helpers.js';
import { onKillGap, runTriggerFamily, RANGE_SELF } from '../ability/trigger-executor.js';
import { removeStatusFromRow, statusItemId, statusRows, statusEndSeconds, statusPaused, statusRemovalBefore, STATUS_IDS, type ExpiryReaders } from './expiry.js';
import { statusRemovalRuntime, applyStatusRemovalRuntime } from '../compile/status-removal-runtime.js';
import { skipUnported } from '../unported.js';
import { mergeSpawnPlans, spawnPlanHasEffects, type SpawnRowPlan } from '../spawn/row-plan';
import { effectAttempt } from '../ability/boundary-checkpoint.js';
import { carrierHasTrigger } from '../ability/statuses/status-trigger-gate.js';
import { HOSTILE_PAOE_ROLL_TYPES } from '../ability/aoe/hostile-roll-types.js';
import { runPaoeEntry, type PaoeEntryStep } from '../ability/paoe-entry.js';
import { resolveCreatureLife } from './gms-tools.js';
import { resolveRemovalSupport } from './removal-support.js';
import { isOperatorStatus, isTransportStatus, transportInventories } from './riding-removal.js';
export { resolveRemovalSupport } from './removal-support.js';
import { encounterField as ftEncounterField } from '../../shared/array-types.js';
import { runPaoeEntryOnce, type PaoeEntryRecords } from '../ability/paoe-entry-once.js';
import { surfaceEntryLogTime } from '../ability/surface-entry-time.js';
import { resolveExactRemovalTails, resolveTemporaryChargeRemovals } from './exact-removal-tails.js';
import { isZoneDefinition, resolveZonePlacement, resolveZoneOccupants } from './zone-add.js';
import { placeSelfFollowAnchor } from '../ability/persistent-aoe.js';
import { phpJsonTruthy } from '../compile/php-json-values.js';
import { planResourceRecovery, planRecoveryBodies, type RecoveryChanges } from '../resources/resource-recovery-plan.js';
import type { StatusEnds } from '../ability/statuses/get-status-effect-duration-times.js';
import { retirePaintedWallSections } from './painted-wall-removal';
import { isFormStatus, prepareFormStatusRemoval } from './shapeshift-inventory';
import { movementDecisionView } from '../movement/decision-view.js';

export interface GmBoundaryVerdict {
  inventoryDelete?: import('../inventory/delete-plan').InventoryDelete;
  recovery?: RecoveryChanges;
  spawnRows?: SpawnRowPlan;
  rows: LogRow[]; ec: Record<string, Row>; statusesAdded: JanusCtx['statusesAdded'];
  statusesRemoved: JanusCtx['statusesRemoved']; statusStacks: NonNullable<JanusCtx['statusStacks']>;
  charges: JanusCtx['chargesChanged']; playerCharactersChanged: NonNullable<JanusCtx['playerCharactersChanged']>;
  chargesRemoved?: JanusCtx['chargesRemoved'];
  fortunateOffers?: string[];
  memberExpectedRevs?: Record<string, number>;
  inventory?: JanusCtx['inventoryChanged'];
  shapeshiftInventory?: JanusCtx['shapeshiftInventory'];
  statusTriggerListeners?: JanusCtx['statusTriggerListeners'];
}
export type GmBoundaryAction =
  | { kind: 'add'; targetId: string; itemId: string; casterId: string; originId?: string; durationInt: string; durationType: string; ends?: StatusEnds; rollPrivate?: number; logMessage?: boolean; allowNoChange?: boolean }
  | { kind: 'delete'; targetId: string; statusId: string; scope: 'single' | 'all' | 'all_gm'; logCasterId?: string; logMessage?: boolean }
  | { kind: 'stack'; targetId: string; statusId: string; direction: 'up' | 'down' }
  | { kind: 'life'; targetId: string; action: 'kill' | 'resurrect' | 'destroy' | 'flee'; killerId?: string };

export function boundaryVerdict(ctx: JanusCtx): GmBoundaryVerdict {
  ctx = boundarySource(ctx);
  return { rows: ctx.rows, ...surfaceEntryLogTime(ctx), ec: frameEcChanges(ctx), statusesAdded: ctx.statusesAdded, statusesRemoved: ctx.statusesRemoved,
    statusStacks: ctx.statusStacks ?? [], charges: ctx.chargesChanged, playerCharactersChanged: ctx.playerCharactersChanged ?? [],
    ...(ctx.chargesRemoved?.length ? { chargesRemoved: ctx.chargesRemoved } : {}),
    ...(spawnPlanHasEffects(ctx.spawnRows) ? { spawnRows: ctx.spawnRows } : {}),
    ...(ctx.statusTriggerListeners?.length ? { statusTriggerListeners: ctx.statusTriggerListeners } : {}),
    ...(ctx.monsterBitRemovals?.length ? { recovery: { bodies: [], monsterBitRemovals: ctx.monsterBitRemovals } } : {}),
    ...(ctx.inventoryChanged.length ? { inventory: ctx.inventoryChanged } : {}),
    ...(ctx.shapeshiftInventory?.length ? { shapeshiftInventory: ctx.shapeshiftInventory } : {}) };
}

/** Failed effect frames are discarded as a unit. Consent is a pre-draw request, never an unported effect. */
function gmRecorder(ctx: JanusCtx): () => string | null {
  const followups = ['deathTail', 'concentrationBreaks', 'turnTransition'] as const;
  const before = followups.map(key => JSON.stringify(ctx[key])), summonsBefore = JSON.stringify(ctx.summons);
  const inventoryBefore = JSON.stringify(ctx.inventoryChanged);
  return () => {
    if (ctx.summons?.length && ctx.read.spawnSummons) { ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, ctx.read.spawnSummons(ctx, ctx.summons)); ctx.summons = []; }
    return followups.some((key, index) => JSON.stringify(ctx[key]) !== before[index])
      || !ctx.saveBoundaryInventory && JSON.stringify(ctx.inventoryChanged) !== inventoryBefore
      || !ctx.read.spawnSummons && JSON.stringify(ctx.summons) !== summonsBefore ? 'follow-up-persistence-not-modeled' : null;
  };
}
export function boundaryAttempt(ctx: JanusCtx, reason: string, resolve: (frame: JanusCtx) => void, primary = false): boolean {
  return effectAttempt(ctx, reason, (ctx) => {
    const recorder = gmRecorder(ctx);
    resolve(ctx);
    const failure = recorder();
    if (failure) throw new HandBack(failure);
  }, { primary });
}

/** Callers holding entry records share the walk throttle; other relocation callers disclose missing hostile facts. */
export function resolveGmZoneEntry(ctx: JanusCtx, step: PaoeEntryStep, entries?: PaoeEntryRecords): void {
  ctx = journalBoundaryContext(ctx);
  boundaryAttempt(ctx, `gm-relocation:${creatureName(ctx, step.enteringId)}:zone-entry`, () => {
    const definition = ctx.read.definition(step.abilityId);
    if (!definition) throw new HandBack('paoe-entry:definition-not-held');
    if (!entries && HOSTILE_PAOE_ROLL_TYPES.includes(upper(ctx.read.value(definition, 'roll_type'))))
      throw new HandBack('hostile-campaign-time-dedupe-facts-not-held');
    ctx.priorDispatchStatuses = new Set(ctx.statusesAdded);
    if (entries) runPaoeEntryOnce(ctx, step, entries);
    else runPaoeEntry(ctx, step);
    for (const s of ctx.statusesAdded) (s as unknown as Row).status_id ??= ctx.read.uuid();
  });
}

/** Ability exit frames persist the complete removal and its tails together; GM boundaries retain their skip policy. */
export function resolveGmZoneExit(ctx: JanusCtx, strip: { statusId: string; itemId: string; targetId: string }, options: { support?: boolean; linkTeardown?: boolean; abilityFrame?: boolean } = {}): void {
  ctx = journalBoundaryContext(ctx);
  const statusId = upper(strip.statusId), itemId = upper(strip.itemId), targetId = upper(strip.targetId);
  if (ctx.statusesRemoved.some(status => upper(status.status_id) === statusId)) return;
  const owner = ctx.members.get(targetId) ?? heldInventoryRows(ctx).get(targetId);
  const held = ctx.read.statuses(owner ?? ctx.encounter).find(s => upper(s.status_id) === statusId);
  const before = held ? deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()])) : [];
  if (held && owner && ecId(owner)) prepareFormStatusRemoval(ctx, owner, held);
  if (held) retirePaintedWallSections(ctx, held);
  for (const member of [ctx.encounter, ...(owner ? [owner] : []), ...ctx.members.values()]) removeStatusFromRow(member, statusId);
  ctx.statusesRemoved.push({ status_id: statusId, status_item_id: itemId, target: targetId,
    ...(held ? { before: statusRemovalBefore(held, targetId) } : {}) });
  // Item statuses have no creature loss hooks or support tail; the same exact-row save owns their retirement.
  if (owner && !ecId(owner)) return;
  statusTrigger(ctx, LOSS, itemId, targetId, null, options.abilityFrame);
  if (held) {
    departRemovedSummons(ctx, held, before, targetId, !options.abilityFrame);
    if (options.abilityFrame) resolveExactRemovalTails(ctx, owner!, held, { summonTagHandled: !!ctx.read.deathResources,
      formHandled: !!owner && !!ecId(owner) && isFormStatus(ctx, held) });
    else removalTails(ctx, held, targetId, options.linkTeardown || itemId === STATUS_IDS.concentrating, true);
    if (options.support !== false) boundaryAttempt(ctx, `gm-relocation:${creatureName(ctx, targetId)}:support`, (ctx) => resolveRemovalSupport(ctx, targetId, held));
  }
}

export function creatureName(ctx: JanusCtx, id: string): string {
  const member = ctx.members.get(upper(id)) ?? ctx.read.definition(upper(id));
  return String(member ? scalar(ecLean(member, 'encounter_creature_name')) ?? itemField(member, 'item_name') ?? 'Creature' : 'Creature outside the roster');
}

function removalTails(ctx: JanusCtx, status: Row, ownerId: string, linkTeardown = false, departsSummons = false, specialsHandled = false): void {
  const itemId = statusItemId(status), definition = ctx.read.definition(itemId) ?? record(status.status_arr);
  const owner = ctx.members.get(ownerId);
  if (!specialsHandled && owner && definition) boundaryAttempt(ctx, `gm-status-remove:${creatureName(ctx, ownerId)}:removal-specials`, (ctx) => {
    // The link caller enumerates its exact peers and owns each loss hook; never raw-sweep those rows ahead of it.
    if (linkTeardown) resolveTemporaryChargeRemovals(ctx, boundaryValue(ctx, owner), definition, boundaryValue(ctx, status));
    else resolveStatusRemovalSpecials(ctx, boundaryValue(ctx, owner), boundaryValue(ctx, status));
  });
  if (itemId === STATUS_IDS.summon_tag) {
    // The cascade and the GM delete run the tag's teardown once for the whole removal (departRemovedSummons).
    if (!departsSummons) ctx.rows.push(skipUnported(`gm-status-remove:${creatureName(ctx, ownerId)}:summon-tag-teardown-not-modeled`));
    return;
  }
  if (isTransportStatus(status) && ctx.statusesRemoved.some(s => upper(s.status_id) === upper(status.status_id) && s.before)) return;
  if (!linkTeardown && definition && storedStatusHasUnportedTeardown(ctx, status))
    ctx.rows.push(skipUnported(`gm-status-remove:${creatureName(ctx, ownerId)}:special-status-teardown:${String(itemField(definition, 'item_name') ?? 'Unknown status')}`));
  if (itemId === STATUS_IDS.crafting)
    ctx.rows.push(skipUnported(`gm-status-remove:${creatureName(ctx, ownerId)}:crafting-completion-not-ported`));
  if (definition && ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
    ctx.rows.push(skipUnported(`gm-status-remove:${creatureName(ctx, ownerId)}:duration-object-destruction-not-ported`));
}

/** Every inventory row the encounter holds: its floor objects and each member's carried tree, container contents included. */
export function heldInventoryRows(ctx: Pick<JanusCtx, 'encounter' | 'members'>): Map<string, Row> {
  const out = new Map<string, Row>();
  const walk = (value: unknown): void => {
    const listish = record(value);
    const rows = Array.isArray(value) ? value : listish && Object.keys(listish).every(key => /^\d+$/.test(key)) ? Object.values(listish) : [];
    for (const entry of rows) {
      const row = record(entry), id = inventoryId(row);
      if (!row || !/^[0-9A-F-]{36}$/.test(id) || out.has(id)) continue;
      out.set(id, row);
      walk(row.inventory_in_container);
    }
  };
  walk(transportInventories(ctx.encounter));
  for (const member of ctx.members.values()) walk(member.inventory);
  return out;
}

/** An all-connections delete retires every row of the connection, whoever holds it (the legacy
 *  remove_status_effect_status_id_all_connections_to_all_targets): a persistent zone's anchor on the encounter and the connection's
 *  copies on floor or carried items leave as raw rows. Neither holder carries loss hooks or support. */
function removeNonCreaturePeers(ctx: JanusCtx, connection: string, items = heldInventoryRows(ctx)): void {
  const encounterId = upper(ctx.read.value(ctx.encounter, 'encounter_id'));
  const peers = [...connectionSurvivors(ctx, connection, ''),
    ...[...items].flatMap(([holder, row]) => statusRows(row, 'target').map(status => ({ holder, status })))];
  for (const { holder, status } of peers) {
    const statusId = upper(status.status_id);
    if (upper(status.status_connected_id) !== connection || (holder !== encounterId && !items.has(holder))
      || !/^[0-9A-F-]{36}$/.test(statusId) || ctx.statusesRemoved.some(s => upper(s.status_id) === statusId)) continue;
    retirePaintedWallSections(ctx, status);
    for (const row of [ctx.encounter, items.get(holder), ...ctx.members.values()]) if (row) removeStatusFromRow(row, statusId);
    ctx.statusesRemoved.push({ status_id: statusId, status_item_id: statusItemId(status), target: holder, before: statusRemovalBefore(status, holder) });
  }
}

/** handle_summon_tag_status_effect, called once per removal as the legacy removers did: every live summon the removed row reaches
 *  departs. A lane without the death package keeps the named skip. With it, an unmodelled departure refuses the action (its tag is
 *  already retired, so a skip would orphan the summon). Timed boundaries retain their per-effect skip policy. */
function departRemovedSummons(ctx: JanusCtx, removed: Row, before: readonly Row[], ownerId: string, boundary = false): void {
  const primary = !boundary && !!ctx.read.deathResources;
  let summons: Row[] = [];
  effectAttempt(ctx, `gm-status-remove:${creatureName(ctx, ownerId)}:summon-teardown`, () => { summons = removalSummons(ctx, removed, before); },
    { checkpoint: false, primary });
  for (const summon of summons) {
    const name = creatureName(ctx, ecId(summon));
    if (!ctx.read.deathResources) ctx.rows.push(skipUnported(`gm-status-remove:${name}:summon-tag-teardown-not-modeled`));
    else boundaryAttempt(ctx, `gm-status-remove:${name}:summon-departure`, (ctx) => departSummon(ctx, boundaryValue(ctx, summon), before), primary);
  }
}

/** Resolve held holders before mutation; a caster projection alone cannot supply an unloaded peer. */
function connectionCascadePlan(ctx: JanusCtx, selected: Row) {
  const connection = upper(ctx.read.value(selected, 'status_connected_id'));
  if (!connection) throw new HandBack('status-cascade:connection-not-held');
  const members = new Map<string, Row>();
  for (const raw of Array.isArray(ctx.encounter.encounter_creatures_arr) ? ctx.encounter.encounter_creatures_arr : []) {
    const member = record(raw), id = ecId(member);
    if (member && id && !Number(ecLean(member, 'encounter_creature_deleted') ?? 0)) members.set(id, member);
  }
  for (const [id, member] of ctx.members) members.set(id, member);
  const peers = connectionSurvivors(ctx, connection, '', members);
  const inventory = heldInventoryRows({ encounter: ctx.encounter, members });
  const encounterId = upper(ctx.read.value(ctx.encounter, 'encounter_id'));
  if (peers.some(peer => !members.has(peer.holder) && peer.holder !== encounterId && !inventory.has(peer.holder)))
    throw new HandBack('status-cascade:connection-peer-not-held');
  return { connection, members, peers, inventory };
}

/** Damage dry runs and direct concentration callers must refuse an incomplete connection before drawing. */
export function requireConnectionCascadeHeld(ctx: JanusCtx, selected: Row): void {
  connectionCascadePlan(ctx, selected);
}

/** Retire the complete connection before loss hooks; each loser/item fires once, including adopted holders. */
export function resolveConnectionCascade(ctx: JanusCtx, selected: Row, ownerId: string, boundary = false): void {
  ctx = journalBoundaryContext(ctx);
  const { connection, members, peers, inventory } = connectionCascadePlan(ctx, selected);
  const adopted = new Map(peers.filter(peer => members.has(peer.holder) && !ctx.members.has(peer.holder))
    .map(peer => [peer.holder, boundarySource(members.get(peer.holder)!)]));
  if (adopted.size) {
    // Only the connected holders enter the write set. The shared view copies changed branches on write.
    const view = movementDecisionView({}, adopted);
    // Guard baseline references so the journal stores aliases without walking their untouched descendants.
    ctx.originals = new Map([...ctx.originals, ...adopted].map(([id, member]) => [id, boundaryValue(ctx, member)]));
    for (const [id, member] of view.members) ctx.members.set(id, member);
  }
  const before = deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()]));
  const removals = [...ctx.members.values()].flatMap(member => activeStatuses(ctx, member)
    .filter(status => upper(scalar(status.status_connected_id)) === connection).map(status => ({ member, status })));
  const runtime = new Map([...new Set(removals.map(r => r.member))].map(member => {
    const projection = statusRemovalRuntime(member, removals.filter(r => r.member === member).map(r => upper(scalar(r.status.status_id))), ctx.read.definition);
    if (!projection.ok) throw new HandBack('status-compiled-footprint:' + projection.layer);
    return [member, projection] as const;
  }));
  removeNonCreaturePeers(ctx, connection, inventory);
  for (const { member, status } of removals) {
    const id = upper(scalar(status.status_id));
    if (ctx.statusesRemoved.some(s => upper(s.status_id) === id)) continue;
    if (!/^[0-9A-F-]{36}$/.test(id)) throw new HandBack('status-cascade:stored-row-not-held');
    if (isFormStatus(ctx, status)) boundaryAttempt(ctx, `gm-status-remove:${creatureName(ctx, ecId(member))}:form`, ctx =>
      prepareFormStatusRemoval(ctx, ctx.members.get(ecId(member))!, boundaryValue(ctx, status)), !boundary);
    for (const row of [ctx.encounter, ...ctx.members.values()]) removeStatusFromRow(row, id);
    ctx.statusesRemoved.push({ status_id: id, status_item_id: statusItemId(status), target: ecId(member), before: statusRemovalBefore(status, ecId(member)) });
  }
  for (const [member, projection] of runtime) applyStatusRemovalRuntime(member, projection);
  const fired = new Set<string>();
  for (const { member, status } of removals) {
    const targetId = ecId(member), itemId = statusItemId(status), key = `${targetId}:${itemId}`;
    if (fired.has(key)) continue;
    fired.add(key);
    statusTrigger(ctx, LOSS, itemId, targetId, null);
    removalTails(ctx, status, targetId, itemId === STATUS_IDS.concentrating || Number(scalar(status.status_aoe_source) ?? 0) === 1, true);
    boundaryAttempt(ctx, `gm-status-remove:${creatureName(ctx, targetId)}:support`, (ctx) => resolveRemovalSupport(ctx, targetId, status), !boundary);
  }
  // Summon tags may have a different connection: the selection also matches the caster and origin spell.
  departRemovedSummons(ctx, selected, before, ownerId, boundary);
}

/** Only due, unpaused source rows invoke the cascade; occupant durations do not independently end a zone. */
export function dueZoneAnchors(ctx: Pick<JanusCtx, 'encounter' | 'members'> & { read: Pick<JanusCtx['read'], 'value' | 'statuses'> }, read: Pick<ExpiryReaders, 'seconds' | 'nowSeconds'>): Array<{ holder: string; status: Row }> {
  return [[upper(ctx.read.value(ctx.encounter, 'encounter_id')), ctx.encounter] as const,
    ...[...ctx.members].map(([id, row]) => [id, row] as const), ...heldInventoryRows(ctx)]
    .flatMap(([holder, row]) => ctx.read.statuses(row).filter(status => {
      const end = statusEndSeconds(status, read);
      return Number(scalar(status.status_aoe_source) ?? 0) === 1 && !statusPaused(status) && end !== null && end <= read.nowSeconds;
    }).map(status => ({ holder, status })));
}
/** The rows the observing GM's cascade retires: due zone anchors, and out of combat a due, unpaused Concentrating row (the
 * calendar walk leaves its connection here, expiry.ts isDueConcentration). In combat the turn lane keeps concentration. */
export function dueCascadeSources(ctx: Pick<JanusCtx, 'encounter' | 'members'> & { read: Pick<JanusCtx['read'], 'value' | 'statuses'> }, read: Pick<ExpiryReaders, 'seconds' | 'nowSeconds'>): Array<{ holder: string; status: Row }> {
  const zones = dueZoneAnchors(ctx, read);
  if (Number(ftEncounterField(ctx.encounter, 'in_combat') ?? 0) === 1) return zones;
  return [...zones, ...[...ctx.members].flatMap(([holder, row]) => ctx.read.statuses(row).filter(status => {
    const end = statusEndSeconds(status, read);
    // A connection-less row would refuse the whole cascade batch; it keeps the calendar walk's named link-family gate instead.
    return statusItemId(status) === STATUS_IDS.concentrating && !!upper(scalar(status.status_connected_id)) && !statusPaused(status)
      && end !== null && end <= read.nowSeconds;
  }).map(status => ({ holder, status })))];
}
export function resolveDueZoneExpiries(ctx: JanusCtx, read: Pick<ExpiryReaders, 'seconds' | 'nowSeconds'>): GmBoundaryVerdict {
  for (const { holder, status } of dueCascadeSources(ctx, read)) {
    if (ctx.statusesRemoved.some(s => upper(s.status_id) === upper(scalar(status.status_id)))) continue;
    const expired = (row: Row, by: string): void => { ctx.rows.push({ roll_type_1: 'status_effect_expired', roll_type_2: null, roll_result: 0,
      roll_item_id: statusItemId(row), roll_by: upper(scalar(row.status_caster_id)) || by, roll_to: null }); };
    if (Number(scalar(status.status_aoe_source) ?? 0) === 1) { resolveConnectionCascade(ctx, status, holder, true); expired(status, holder); continue; }
    // Concentration: legacy's calendar pass logged every due row it retired, so the spell (not only Concentrating) reads expired.
    const connection = upper(scalar(status.status_connected_id));
    const due = [...ctx.members].flatMap(([id, row]) => ctx.read.statuses(row).filter(s => upper(scalar(s.status_connected_id)) === connection
      && !statusPaused(s) && (statusEndSeconds(s, read) ?? Infinity) <= read.nowSeconds).map(s => ({ holder: id, status: s })));
    resolveConnectionCascade(ctx, status, holder, true);
    const logged = new Set<string>();
    for (const row of due) {
      const key = `${row.holder}:${statusItemId(row.status)}`;
      if (logged.has(key) || !ctx.statusesRemoved.some(s => upper(s.status_id) === upper(scalar(row.status.status_id)))) continue;
      logged.add(key); expired(row.status, row.holder);
    }
  }
  return boundaryVerdict(ctx);
}

/** Departures preserve the pointer actor until the shared End Turn lane has served its boundary. */
export class TurnHolderRefusal extends Refusal {}
export function requireEndedTurn(encounter: Row, ids: Iterable<string>, action: 'destroy' | 'flee' | 'transfer'): void {
  if (Number(ftEncounterField(encounter, 'in_combat') ?? 0) !== 1) return;
  const pointer = upper(ftEncounterField(encounter, 'current_encounter_creature_id_turn'));
  const pending = upper(ftEncounterField(encounter, 'pending_main_turn_encounter_creature_id'));
  if ([...ids].some(id => upper(id) === pointer || (pending && upper(id) === pending)))
    throw new TurnHolderRefusal(`End the turn first before you ${action} its creature.`);
}

const GAIN = 'triggers_when_caster_gains_status_effect', TARGET_GAIN = 'triggers_when_target_gains_status_effect', LOSS = 'triggers_when_caster_loses_status_effect';
function statusTrigger(ctx: JanusCtx, trigger: string, itemId: string, ownerId: string, incitingId: string | null, abilityFrame = false): void {
  if (!ownerId) return; // Environmental applications have no caster-side listener.
  // No listener → no effect attempt. A gate that cannot decide (a definition not held) still runs the frame,
  // which names the skip exactly as before.
  const listener = ctx.members.get(ownerId);
  try { if (listener && !carrierHasTrigger(ctx, listener, trigger, 'gm-status-trigger', itemId)) return; } catch { /* the frame names it */ }
  const resolve = (frame = ctx): void => {
    const ctx = frame;
    const owner = ctx.members.get(ownerId);
    if (!owner) throw new HandBack('owner-not-held');
    if (!carrierHasTrigger(ctx, owner, trigger, 'gm-status-trigger', itemId)) return;
    runTriggerFamily(ctx, { trigger, statusItemId: itemId }, owner, incitingId ? ctx.members.get(incitingId) ?? null : null, [owner], false, undefined, incitingId === null);
  };
  if (abilityFrame) resolve();
  else boundaryAttempt(ctx, `gm-status-trigger:${creatureName(ctx, ownerId)}:${trigger}`, resolve);
}

/** Replay only gains created while the GM write muted triggers; nested hook gains dispatch through the normal applicator. */
function statusGainTriggers(ctx: JanusCtx, addedFrom: number): void {
  for (const added of ctx.statusesAdded.slice(addedFrom)) {
    if (!ctx.members.has(upper(added.target))) continue;
    statusTrigger(ctx, GAIN, added.status_item_id, upper(added.target), upper(added.caster));
    if (upper(added.target) !== upper(added.caster)) statusTrigger(ctx, TARGET_GAIN, added.status_item_id, upper(added.caster), upper(added.target));
  }
}

export const CREATURE_HIDDEN_STATUS = '47667406-1B72-9D60-310B-D8CF6BFE397D';

/** The creature form's quiet Hidden toggle, including its same-target connection cleanup. */
export function resolveCreatureVisibility(ctx: JanusCtx, targetId: string, hidden: boolean): GmBoundaryVerdict {
  ctx = journalBoundaryContext(ctx);
  const id = upper(targetId), target = ctx.members.get(id);
  if (!target) throw new HandBack('creature-visibility:target-not-held');
  const held = activeStatuses(ctx, target).filter(s => statusItemId(s) === CREATURE_HIDDEN_STATUS);
  if (hidden && !held.length) {
    ctx.statusTriggersMuted = true;
    try {
      boundaryAttempt(ctx, `creature-hide:${creatureName(ctx, id)}`, () => {
        const input = { abilityId: CREATURE_HIDDEN_STATUS, casterId: id, targetIds: [id], rawDiceMod: 0,
          rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' };
        applyStatusEffect(ctx, input, {}, CREATURE_HIDDEN_STATUS, 'self_status_effect_applied', target, target,
          { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, false, false, 0, false, '');
      }, true);
    } finally { ctx.statusTriggersMuted = false; }
    statusGainTriggers(ctx, 0);
  } else if (!hidden) {
    for (const status of held) {
      // The legacy reveal removes this target's connection peers, never another holder's rows.
      const connection = upper(scalar(status.status_connected_id));
      const peers = activeStatuses(ctx, target).filter(s => upper(scalar(s.status_connected_id)) === connection);
      resolveGmZoneExit(ctx, { statusId: upper(status.status_id), itemId: CREATURE_HIDDEN_STATUS, targetId: id });
      for (const peer of peers) {
        const statusId = upper(peer.status_id);
        if (ctx.statusesRemoved.some(s => upper(s.status_id) === statusId)) continue;
        removeStatusFromRow(target, statusId);
        ctx.statusesRemoved.push({ status_id: statusId, status_item_id: statusItemId(peer), target: id, before: statusRemovalBefore(peer, id) });
      }
    }
  }
  for (const added of ctx.statusesAdded) added.status_id ??= ctx.read.uuid();
  return boundaryVerdict(ctx);
}

/** The GM tool's primary state and supported triggers are decided on the same browser snapshot. */
export function resolveGmBoundary(ctx: JanusCtx, action: GmBoundaryAction): GmBoundaryVerdict {
  ctx = journalBoundaryContext(ctx);
  const targetId = upper(action.targetId), target = ctx.members.get(targetId);
  if (!target) throw new HandBack('gm-boundary:target-not-held');
  if (action.kind === 'add') {
    ctx.rollPrivate = action.rollPrivate ?? 0;
    const itemId = upper(action.itemId), casterId = upper(action.casterId) || targetId;
    const caster = ctx.members.get(casterId), def = ctx.read.definition(itemId);
    if (!caster || !def) throw new HandBack('gm-boundary:add-definition-or-caster-not-held');
    const duration = { items: { item_id: itemId }, item_layer_types: { duration_int: action.durationInt, duration_type: action.durationType } };
    // The form specifies its own duration; the status item's authored duration does not replace it.
    const read = ctx.read;
    const durationRead = { ...read, value: (r: Row | null, k: string) => r === duration ? record(duration.item_layer_types)?.[k] ?? null : read.value(r, k),
      has: (r: Row | null, k: string) => r === duration ? k in duration.item_layer_types : read.has(r, k) };
    ctx.read = durationRead;
    ctx.statusTriggersMuted = true;
    const addedFrom = ctx.statusesAdded.length, removedFrom = ctx.statusesRemoved.length;
    try { boundaryAttempt(ctx, 'gm-status-add', () => {
      const added = ctx.statusesAdded.length, stacks = ctx.statusStacks?.length ?? 0, logs = ctx.rows.length;
      const input = { abilityId: itemId, casterId, targetIds: [targetId], rawDiceMod: 0, rawDiceAdvDisStacks: 0,
        useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' };
      if (isZoneDefinition(def, (row, key) => read.value(row, key))) {
        // Self-follow auras use the existing placement and initial-occupant planners. Other zone placements retain their refusal.
        if (!phpJsonTruthy(read.value(def, 'aoe_follows_target')) || upper(read.value(def, 'range')) !== RANGE_SELF)
          throw new HandBack('zone-add-plan-not-modeled');
        const encounter = { ...ctx.encounter, encounter_creatures_arr: [...ctx.members.values()] };
        const zoneInput = { encounterId: upper(ftEncounterField(ctx.encounter, 'encounter_id')), encounter,
          targetId, caster, definition: def, layer: (row: Row, key: string) => read.value(row, key),
          occupancy: (row: Row) => read.auraOccupancy?.(row) };
        const plan = resolveZonePlacement(zoneInput);
        if (!plan) throw new HandBack('zone-add-plan-not-modeled');
        const aura = { ...def, item_layer_types: { ...record(def.item_layer_types), ...duration.item_layer_types } };
        input.castingTargets = plan.span;
        placeSelfFollowAnchor(ctx, input, aura, caster, caster, true);
        // Placement validates and creates this anchor; its saved dimensions include the carrier's size credit.
        const anchor = ctx.statusesAdded.find(status => status.target === casterId && status.status_item_id === itemId
          && status.status_connected_id === ctx.statusConnectedId && status.status_aoe_source === 1)!;
        const occupants = resolveZoneOccupants(zoneInput, { status: anchor as unknown as Row, key: '', holderId: casterId });
        const items = heldInventoryRows(ctx);
        if (occupants.some(id => !ctx.members.has(id) && !items.has(id))) throw new HandBack('zone-add-plan-not-modeled');
        for (const id of new Set([targetId, ...occupants])) {
          if (id === casterId) continue; // the anchor already carries the payload
          applyStatusEffect(ctx, input, duration, itemId, 'self_status_effect_applied', caster, ctx.members.get(id) ?? items.get(id)!);
        }
      } else {
        // A status UUID resolves to no source payload; maintenance callers retain their explicit stamp.
        applyStatusEffect(ctx, input, duration, itemId, 'self_status_effect_applied', caster, target,
          action.ends, false, action.logMessage !== false, 0, false, action.originId);
        if (action.originId === undefined) for (const status of ctx.statusesAdded.slice(added))
          status.status_origin_id = status.status_id ??= ctx.read.uuid();
      }
      if (!action.allowNoChange && ctx.statusesAdded.length === added && (ctx.statusStacks?.length ?? 0) === stacks
          && !ctx.rows.slice(logs).some(row => row.roll_item_id === itemId
            && (row.roll_type_1 === 'target_immune_to_status_effect' || row.roll_type_1 === 'self_status_effect_applied')))
        throw new Refusal('The status was not added: its application produced no status change.');
    }, true); } finally { ctx.statusTriggersMuted = false; ctx.read = read; }
    // Automatic dismount/unpilot retains the operator's loss hook; reciprocal retirement has no independent hook.
    for (const removed of ctx.statusesRemoved.slice(removedFrom).filter(isOperatorStatus))
      statusTrigger(ctx, LOSS, removed.status_item_id, upper(removed.target), null);
    // The status write survives each independent trigger frame's named skip.
    statusGainTriggers(ctx, addedFrom);
  } else if (action.kind === 'stack') {
    const held = activeStatuses(ctx, target).find(s => upper(s.status_id) === upper(action.statusId));
    if (!held) throw new HandBack('gm-stack:status-not-held');
    const itemId = statusItemId(held), family = QUANTITY_FAMILIES[itemId];
    if (!family) ctx.rows.push(skipUnported('gm-stack:stage-machine-not-modeled'));
    else {
      const before = Math.trunc(Number(scalar(held.status_stack) ?? 1)) || 1;
      const next = Math.min(family.max, Math.max(0, before + (action.direction === 'up' ? 1 : -1)));
      if (!next) return resolveGmBoundary(ctx, { kind: 'delete', targetId, statusId: action.statusId, scope: 'single' });
      if (family.overflow && before === family.max && action.direction === 'up') ctx.rows.push(skipUnported('gm-stack:quantity-overflow-tail'));
      else {
        (ctx.statusStacks ??= []).push({ status_id: upper(held.status_id), status_item_id: itemId, target: targetId, before, status_stack: next });
        ctx.rows.push({ roll_type_1: next > before ? 'status_stack_increased' : 'status_stack_decreased', roll_type_2: null,
          roll_result: 0, roll_item_id: itemId, roll_by: targetId, roll_to: targetId });
      }
    }
  } else if (action.kind === 'delete') {
    const held = activeStatuses(ctx, target).find(s => upper(s.status_id) === upper(action.statusId));
    if (!held) throw new HandBack('gm-boundary:status-not-held');
    const connection = upper(held.status_connected_id);
    // Ending concentration ends its spell: a single delete of Concentrating removes the whole connection too (owner 2026-10-13).
    const allConnections = action.scope !== 'single' || statusItemId(held) === STATUS_IDS.concentrating;
    if (allConnections && connection && !isTransportStatus(held)) {
      resolveConnectionCascade(ctx, held, targetId);
      ctx.rows.push({ roll_type_1: 'self_status_effect_all_connections_deleted', roll_type_2: null, roll_result: 0,
        roll_item_id: statusItemId(held), roll_by: upper(action.logCasterId) || targetId, roll_to: null });
      return boundaryVerdict(ctx);
    }
    const before = deathStatusRows(deathHolders([ctx.encounter, ...ctx.members.values()]));
    // Riding/Piloting tools retain their dedicated reciprocal placement and support plan.
    if (allConnections && connection) removeNonCreaturePeers(ctx, connection);
    const peers = connectionSurvivors(ctx, connection, upper(held.status_id));
    const removals = [...ctx.members.values()].flatMap(member => activeStatuses(ctx, member).filter(s =>
      upper(s.status_id) === upper(action.statusId) || (allConnections && connection && upper(s.status_connected_id) === connection))
      .map(status => ({ member, status })));
    for (const { member, status } of removals) {
      if (!activeStatuses(ctx, ctx.members.get(ecId(member))!).some(s => upper(s.status_id) === upper(status.status_id))) continue;
      const count = ctx.statusesRemoved.length;
      const transport = isTransportStatus(status);
      const definition = ctx.read.definition(statusItemId(status)) ?? record(status.status_arr);
      const lossInRemoval = !transport && !!definition && storedStatusHasSpecialTeardown(ctx, status);
      let specialsHandled = false;
      let addedFrom = ctx.statusesAdded.length;
      ctx.statusTriggersMuted = true;
      try {
        if (transport || (!peers.some(peer => !peer.member) && definition && !storedStatusHasUnportedTeardown(ctx, status)))
          specialsHandled = boundaryAttempt(ctx, `gm-status-remove:${creatureName(ctx, ecId(member))}`, (ctx) =>
            removeStoredStatus(ctx, ctx.members.get(ecId(member))!, boundaryValue(ctx, status), 'preserve', lossInRemoval ? {
              // Keep expiry's loss-before-specials order while each GM listener retains its independent skip frame.
              onLoss: (ctx, owner, itemId) => {
                ctx.statusTriggersMuted = false;
                try { statusTrigger(ctx, LOSS, itemId, ecId(owner), null); }
                finally { addedFrom = ctx.statusesAdded.length; ctx.statusTriggersMuted = true; }
              },
            } : {}), transport);
      } finally { ctx.statusTriggersMuted = false; }
      statusGainTriggers(ctx, addedFrom);
      if (ctx.statusesRemoved.length === count) {
        // Unsupported removal tails stay independent: retire the selected row, then serve or name each remaining duty.
        const id = upper(status.status_id), owner = ctx.members.get(ecId(member))!;
        prepareFormStatusRemoval(ctx, owner, status);
        removeStatusFromRow(owner, id);
        ctx.statusesRemoved.push({ status_id: id, status_item_id: statusItemId(status), target: ecId(member) });
      }
      for (const removed of ctx.statusesRemoved.slice(count)) {
        // The shared grapple tail raw-deletes its inverse/restraint; only the selected row owns a loss hook.
        const sweptGrapple = specialsHandled && (statusItemId(status) === STATUS_IDS.grappled || statusItemId(status) === STATUS_IDS.grappling)
          && upper(removed.status_id) !== upper(status.status_id);
        const selectedLossHandled = specialsHandled && lossInRemoval && upper(removed.status_id) === upper(status.status_id);
        if (!selectedLossHandled && !sweptGrapple && !(removed.before && (removed.status_item_id === STATUS_IDS.ridden || removed.status_item_id === STATUS_IDS.piloted) && statusItemId(status) !== removed.status_item_id))
          statusTrigger(ctx, LOSS, removed.status_item_id, upper(removed.target), null);
      }
      removalTails(ctx, status, ecId(member), false, true, specialsHandled && !transport);
      if (!transport) boundaryAttempt(ctx, `gm-status-remove:${creatureName(ctx, ecId(member))}:support`, (ctx) => resolveRemovalSupport(ctx, ecId(member), status), true);
    }
    departRemovedSummons(ctx, held, before, targetId);
    // Single-row removal deliberately preserves peers; a connection delete owes their teardown.
    if (allConnections && peers.some(peer => !ctx.statusesRemoved.some(s => upper(s.status_id) === upper(peer.status.status_id))))
      ctx.rows.push(skipUnported(`gm-status-remove:${creatureName(ctx, targetId)}:connection-peer-teardown-not-modeled`));
    if (action.logMessage !== false) ctx.rows.push({ roll_type_1: allConnections ? 'self_status_effect_all_connections_deleted' : 'self_status_effect_deleted',
      roll_type_2: null, roll_result: 0, roll_item_id: statusItemId(held), roll_by: upper(action.logCasterId) || targetId, roll_to: null });
  } else {
    const life = resolveCreatureLife(target, { action: action.action });
    if (!life.supported) {
      if (life.refused) throw new Refusal(life.message);
      throw new HandBack(`gm-boundary:${life.reason}`);
    }
    if (action.action === 'destroy' || action.action === 'flee') requireEndedTurn(ctx.encounter, [targetId], action.action);
    if (action.action === 'destroy' || action.action === 'flee') {
      composeCreatureDeletion22(ctx, target, action.action);
      ctx.rows.push({ roll_type_1: action.action === 'flee' ? 'creature_fled' : 'creature_destroyed',
        roll_type_2: null, roll_result: 0, roll_item_id: null, roll_by: targetId, roll_to: null });
      const verdict = boundaryVerdict(ctx);
      (verdict.ec[targetId] ??= {}).encounter_creature_deleted = 1;
      return verdict;
    }
    if (action.action === 'kill' && life.before.dead === 1) return boundaryVerdict(ctx);
    if (action.action === 'resurrect') {
      if (life.before.dead === 0) {
        const bodies = Number(ecLean(target, 'v_current') ?? 0) > 0
          ? planRecoveryBodies(ctx, targetId, heldInventoryRows(ctx), ctx.read, false) : [];
        const verdict = boundaryVerdict(ctx);
        if (bodies.length) verdict.recovery = { bodies, monsterBitRemovals: [] };
        return verdict;
      }
      let recovery: RecoveryChanges | undefined;
      boundaryAttempt(ctx, `gm-resurrect:${creatureName(ctx, targetId)}`, () => {
        recovery = planResourceRecovery(ctx, targetId, { v_current: Math.max(1, Number(ecLean(target, 'v_current') ?? 0)) },
          heldInventoryRows(ctx), ctx.read);
      }, true);
      const verdict = boundaryVerdict(ctx);
      (verdict.ec[targetId] ??= {}).encounter_creature_deleted = 0;
      verdict.recovery = recovery;
      ctx.rows.push({ roll_type_1: 'creature_resurrected', roll_type_2: null, roll_result: 0, roll_item_id: null, roll_by: targetId, roll_to: null });
      for (const status of verdict.statusesAdded) status.status_id ??= ctx.read.uuid();
      return verdict;
    }
    const deathComposed = action.action === 'kill' && boundaryAttempt(ctx, `gm-kill:${creatureName(ctx, targetId)}`, () => {
      const killer = ctx.members.get(upper(action.killerId));
      composeActualDeath(ctx, killer ?? target, target, target, 0, { killMessage: 'creature_died', creditKiller: !!killer });
    });
    const result = ec(target);
    result.encounter_creature_dead = 1;
    result.v_current = 0;
    if (action.action === 'kill' && action.killerId) {
      const killer = ctx.members.get(upper(action.killerId));
      // Each due kill carrier owns its own effect frame; an unsupported one does not discard its supported peers.
      if (killer) {
        const read = ctx.read;
        ctx.read = { ...read, killTriggerRecorder: gmRecorder };
        try { effectAttempt(ctx, `gm-kill-trigger:${creatureName(ctx, ecId(killer))}`,
          () => { runTriggerFamily(ctx, { trigger: 'triggers_on_kill' }, killer, target, [target], false, onKillGap); }, { checkpoint: false }); }
        finally { ctx.read = read; }
      }
      else ctx.rows.push(skipUnported('gm-kill-trigger:killer-not-held'));
    }
    if (!deathComposed) ctx.rows.push({ roll_type_1: 'creature_died',
      roll_type_2: null, roll_result: 0, roll_item_id: null, roll_by: targetId, roll_to: null });
  }
  const verdict = boundaryVerdict(ctx);
  for (const status of verdict.statusesAdded) status.status_id ??= ctx.read.uuid();
  return verdict;
}
